import classNames from "classnames";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./TagList.scss";

const MaxNonHighlightedTagsToShowInline = 6;

interface TagListProps<T extends string> {
  className?: string;
  tags: T[];
  sort?: boolean;
  thingRoute?: string;
  thingName?: { singular: string; plural: string };
  highlight?: string;
  mapTagToText?: (tag: T) => string;
  showHighlightedTagsFirst?: boolean;
  collapsible?: boolean;
}

export const TagList = <T extends string>({
  className,
  tags,
  sort,
  thingRoute,
  thingName,
  highlight,
  mapTagToText,
  showHighlightedTagsFirst,
  collapsible,
}: TagListProps<T>) => {
  const [showAllTags, setShowAllTags] = useState(false);

  const contents = useMemo(() => {
    const filteredTags = tags.filter((tag) => !!tag?.trim());

    const tagInfo = filteredTags.map((tag) => ({
      tag,
      label: mapTagToText ? mapTagToText(tag) : tag,
      highlighted: highlight?.toLowerCase() === tag.toLowerCase(),
    }));

    let sortedTagInfo = sort
      ? tagInfo.sort((a, b) => a.label.localeCompare(b.label))
      : tagInfo;

    const highlightedTags = sortedTagInfo.filter(
      ({ highlighted }) => highlighted,
    );
    const otherTags = sortedTagInfo.filter(({ highlighted }) => !highlighted);

    if (showHighlightedTagsFirst) {
      sortedTagInfo = [...highlightedTags, ...otherTags];
    }

    const truncateTagList =
      !showAllTags &&
      collapsible &&
      otherTags.length > MaxNonHighlightedTagsToShowInline + 1;
    if (truncateTagList) {
      sortedTagInfo = [
        ...highlightedTags,
        ...otherTags.slice(0, MaxNonHighlightedTagsToShowInline),
      ];
    }

    let showAllTagsText = "Show all";
    if (thingName) {
      const numHidden = otherTags.length - MaxNonHighlightedTagsToShowInline;
      showAllTagsText = `...and ${numHidden} more ${numHidden === 1 ? thingName.singular : thingName.plural}`;
    }

    const showAllTagsElement = truncateTagList ? (
      <div
        key="show-all-tags"
        className="tag-list-show-all-tags"
        onClick={() => setShowAllTags(true)}
      >
        {showAllTagsText}
      </div>
    ) : null;

    if (thingRoute) {
      return [
        ...sortedTagInfo.map(({ tag, label, highlighted }, i) => {
          const tagClassNames = classNames("tag-list-tag", {
            "tag-list-highlighted": highlighted,
            "tag-list-link": !!thingRoute,
          });

          return (
            <Link
              to={thingRoute.replace("$VALUE", encodeURIComponent(tag))}
              className={tagClassNames}
              key={`tag-${tag}-${i}`}
            >
              {label}
            </Link>
          );
        }),
        showAllTagsElement,
      ];
    }

    return [
      ...sortedTagInfo.map(({ tag, label, highlighted }, i) => {
        const tagClassNames = classNames("tag-list-tag", {
          "tag-list-highlighted": highlighted,
          "tag-list-link": !!thingRoute,
        });

        return (
          <div className={tagClassNames} key={`tag-${tag}-${i}`}>
            {label}
          </div>
        );
      }),
      showAllTagsElement,
    ];
  }, [
    tags,
    sort,
    showHighlightedTagsFirst,
    showAllTags,
    collapsible,
    thingName,
    thingRoute,
    mapTagToText,
    highlight,
  ]);

  return <div className={classNames("tag-list", className)}>{contents}</div>;
};
